import { Injectable, signal } from '@angular/core';
import type { CrmPermissionModulesPayload } from './crm-auth-api.service';

const CRM_PERMISSION_STATE_KEY = 'crm.permission-state';

@Injectable({ providedIn: 'root' })
export class CrmPermissionStateService {
  /** Signal-backed so the `computed()` permission flags across the app
   *  re-evaluate when permissions land. A plain field would be read once by each
   *  computed and cached forever, so a late refresh (bootstrap re-fetch after a
   *  browser restart) would never reach the UI and every gated button would stay
   *  disabled for the rest of the session. */
  private readonly state = signal<CrmPermissionModulesPayload | null>(null);

  constructor() {
    this.hydrate();
  }

  /** Forces an explicit re-hydration of the in-memory state from storage.
   *  Call this on app/bootstrap navigation to ensure permission data is available
   *  before any computed signals or UI bindings access the service. */
  hydrate(): void {
    if (typeof window === 'undefined') {
      return;
    }

    // Falls back to the pre-migration sessionStorage location so a tab that is
    // already open when this ships keeps its permissions.
    const raw =
      localStorage.getItem(CRM_PERMISSION_STATE_KEY) ??
      sessionStorage.getItem(CRM_PERMISSION_STATE_KEY);
    if (!raw) {
      return;
    }

    try {
      const parsed = JSON.parse(raw);
      const normalized = this.toModulesPayload(parsed);
      if (normalized) {
        this.state.set(normalized);
      }
    } catch {
      localStorage.removeItem(CRM_PERMISSION_STATE_KEY);
      sessionStorage.removeItem(CRM_PERMISSION_STATE_KEY);
    }
  }

  setPermissions(payload: unknown): void {
    const normalized = this.toModulesPayload(payload);
    this.state.set(normalized);

    if (typeof window === 'undefined') {
      return;
    }

    sessionStorage.removeItem(CRM_PERMISSION_STATE_KEY);

    if (!normalized) {
      localStorage.removeItem(CRM_PERMISSION_STATE_KEY);
      return;
    }

    // localStorage, not sessionStorage: the session itself (auth cookies +
    // `wealthmax.session`) survives a browser restart, so permissions kept in
    // sessionStorage came back empty and every create/edit/delete button
    // rendered disabled on a page that otherwise loaded fine.
    localStorage.setItem(CRM_PERMISSION_STATE_KEY, JSON.stringify(normalized));
  }

  clear(): void {
    this.state.set(null);

    if (typeof window !== 'undefined') {
      localStorage.removeItem(CRM_PERMISSION_STATE_KEY);
      sessionStorage.removeItem(CRM_PERMISSION_STATE_KEY);
    }
  }

  getAllPermissions(): CrmPermissionModulesPayload | null {
    return this.getPermissions();
  }

  getModulePageKeys(moduleKey: string): string[] {
    const modules = this.getPermissions();
    if (!modules) return [];
    const moduleNode = this.toRecord(modules[moduleKey]);
    if (!moduleNode) return [];
    return Object.keys(moduleNode);
  }

  hasSupportedInModule(moduleKey: string): boolean {
    const modules = this.getPermissions();
    if (!modules) {
      return false;
    }

    const moduleNode = this.toRecord(modules[moduleKey]);
    if (!moduleNode) {
      return false;
    }

    return Object.values(moduleNode).some((pageNode) => this.hasSupportedInNode(pageNode));
  }

  hasSupportedInPage(moduleKey: string, pageKey: string): boolean {
    const modules = this.getPermissions();
    if (!modules) {
      return false;
    }

    const moduleNode = this.toRecord(modules[moduleKey]);
    if (!moduleNode) {
      return false;
    }

    return this.hasSupportedInNode(moduleNode[pageKey]);
  }

  getPageActions(moduleKey: string, pageKey: string): string[] {
    const modules = this.getPermissions();
    if (!modules) {
      return [];
    }

    const moduleNode = this.toRecord(modules[moduleKey]);
    if (!moduleNode) {
      return [];
    }

    const pageNode = this.toRecord(moduleNode[pageKey]);
    if (!pageNode) {
      return [];
    }

    const rawPermissions = pageNode['permissions'];
    if (Array.isArray(rawPermissions)) {
      const actions = rawPermissions
        .filter((permission) => typeof permission === 'string')
        .map((permission) => permission.trim().toLowerCase())
        .filter((permission) => permission.length > 0);

      return [...new Set(actions)];
    }

    const permissionsNode = this.toRecord(rawPermissions);
    if (!permissionsNode) {
      return [];
    }

    const actions: string[] = [];
    for (const [action, actionNode] of Object.entries(permissionsNode)) {
      const actionRecord = this.toRecord(actionNode);
      if (!actionRecord) {
        continue;
      }

      const supported = this.toBoolean(actionRecord['supported']);
      const enabled = this.toBoolean(actionRecord['enabled']);

      if (supported || enabled) {
        actions.push(action);
      }
    }

    return actions;
  }

  /** Pure read. Hydration happens in the constructor and in `hydrate()`, never
   *  here — writing the signal on read would throw inside the `computed()`
   *  permission flags that call into this service. */
  private getPermissions(): CrmPermissionModulesPayload | null {
    return this.state();
  }

  private hasSupportedInNode(node: unknown): boolean {
    const nodeRecord = this.toRecord(node);
    if (!nodeRecord) {
      return false;
    }

    const rawPermissions = nodeRecord['permissions'];
    if (Array.isArray(rawPermissions)) {
      return rawPermissions.some((permission) => typeof permission === 'string' && permission.trim().length > 0);
    }

    const permissionsNode = this.toRecord(nodeRecord['permissions']);
    if (permissionsNode) {
      for (const actionNode of Object.values(permissionsNode)) {
        const actionRecord = this.toRecord(actionNode);
        if (!actionRecord) {
          continue;
        }

        if (this.toBoolean(actionRecord['supported'])) {
          return true;
        }
      }
    }

    for (const [key, value] of Object.entries(nodeRecord)) {
      if (key === 'permissions') {
        continue;
      }

      if (this.hasSupportedInNode(value)) {
        return true;
      }
    }

    return false;
  }

  private toModulesPayload(value: unknown): CrmPermissionModulesPayload | null {
    const root = this.toRecord(value);
    if (!root) {
      return null;
    }

    const normalized: Record<string, Record<string, unknown>> = {};

    for (const [moduleKey, moduleValue] of Object.entries(root)) {
      const moduleRecord = this.toRecord(moduleValue);
      if (!moduleRecord) {
        continue;
      }

      normalized[moduleKey] = moduleRecord;
    }

    return Object.keys(normalized).length > 0
      ? (normalized as unknown as CrmPermissionModulesPayload)
      : null;
  }

  private toRecord(value: unknown): Record<string, unknown> | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return null;
    }

    return value as Record<string, unknown>;
  }

  private toBoolean(value: unknown): boolean {
    if (typeof value === 'boolean') {
      return value;
    }

    if (typeof value === 'string') {
      return value.trim().toLowerCase() === 'true';
    }

    if (typeof value === 'number') {
      return value === 1;
    }

    return false;
  }
}
